import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  Card,
  CardContent,
  Autocomplete,
} from "@mui/material";
import axios from "axios";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ImageIcon from "@mui/icons-material/Image";
import Webcam from "react-webcam";

const WeeklyBuildingAudit = ({ onClose, setOpenDefaultSnackbar, selectedTask }) => {
  const externalQuestions = [
    "Are all the walkways clear from debris?",
    "Are all walkways even and free from damage?",
    "Are all gates secure and working properly?",
    "Are all walls and dividing fences free from damage?",
    "Are all drains secure and working properly?",
    "Are all down spouts intact?",
    "Is all guttering intact?",
    "Is all masonry and stonework intact?",
    "Are all roofing slates in place?",
    "Is all outdoor storage in good order?",
  ];

  const internalQuestions = [
    "Is all flooring and floor covering level and free from damage?",
    "Are staircases free from damage?",
    "Is lighting satisfactory?",
    "Can the temperature be controlled?",
    "Are all radiators secured properly to the wall and working?",
    "Do all door locks operate correctly?",
    "Do all windows operate correctly?",
    "Are upper floor windows fitted with restrictors?",
    "Do all toilets operate correctly?",
  ];
  const user = useSelector((state) => state.auth.user);
  const today = new Date();
  const currentDate = today.toISOString().split("T")[0];
  const [section, setSection] = useState("External");
  const questions = section === "External" ? externalQuestions : internalQuestions;
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [responses, setResponses] = useState([]);
  const [condition, setCondition] = useState(null);
  const [comments, setComments] = useState("");
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [maintenanceInformed, setMaintenanceInformed] = useState(null);
  const [dateInformed, setDateInformed] = useState("");
  const [informerName, setInformerName] = useState("");
  const [imageSrc, setImageSrc] = useState(null);
  const fileInputRef = useRef(null);
  const webcamRef = useRef(null);
  const [damageType, setDamageType] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [records, setRecords] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [taskId, setTaskId] = useState(selectedTask?.id);
  const [completedSections, setCompletedSections] = useState({ External: false, Internal: false });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  const handleDamageTypeChange = (event, newType) => {
    if (newType !== null) {
      setDamageType(newType);
    }
  };

  const handleFileUpload = (event) => {
    setAttachment(event.target.files[0]);
  };

  // Handle file selection
  // const handleFileUpload = (event) => {
  //   const file = event.target.files[0];
  //   if (file) {
  //     const reader = new FileReader();
  //     reader.onload = (e) => setImageSrc(e.target.result);
  //     reader.readAsDataURL(file);
  //     setIsCameraOpen(false); // Close camera if file is selected
  //   }
  // };

  // Handle camera capture
  const capture = useCallback(() => {
    if (webcamRef.current) {
      const image = webcamRef.current.getScreenshot();
      setImageSrc(image);
      setIsCameraOpen(false); // Close the camera after capturing
    }
  }, []);

  const handleSubmitDamage = () => {
    if (!damageType || !comments) {
      alert("Please fill out all fields before submitting.");
      return;
    }

    // Create new damage record
    const newRecord = {
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      date: currentDate,
      damageType: damageType,
      comments: comments,
      attachment: attachment ? attachment.name : imageSrc,
    };

    // Update the records state with the new damage entry
    setRecords([...records, newRecord]);

    // Clear form inputs and close dialog
    setDamageType("");
    setComments("");
    setAttachment(null);
    setImageSrc(null);
    setDialogOpen(false);
  };

  const handleConditionChange = (event, newCondition) => {
    setCondition(newCondition);
  };

  const handleNext = () => {
    setResponses([
      ...responses,
      {
        section,
        question: questions[currentQuestionIndex],
        condition,
        comments,
        date: selectedDate,
      },
    ]);
    setCondition(null);
    setComments("");

    if (currentQuestionIndex < questions.length) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    // Prepare damage records list
    const damageRecords = records.map((record) => ({
      time: record.time,
      date: record.date,
      damageType: record.damageType,
      comments: record.comments,
      attachment: record.attachment,
    }));

    const payload = {
      questionType: section,
      questions: responses,
      damageRecords,
      completed_by: user.username,
      dateInformed,
      maintenanceInformed,
      informerName,
    };

    try {
      await axios.post("/api/audit/buildingAudit", payload);

      // Mark the current section as completed
      setCompletedSections((prev) => ({
        ...prev,
        [section]: true,
      }));

      // Reset state after submission
      setResponses([]);
      setCondition(null);
      setComments("");
      setDateInformed("");
      setMaintenanceInformed(null);
      setInformerName("");
      setSelectedDate(currentDate);
      setRecords([]);
      setCurrentQuestionIndex(0);
      setSelectedAction("");
      setAttachment(null);
      setImageSrc(null);
      setDamageType("");
    } catch (error) {
      console.error("Error submitting responses:", error);
      alert("Failed to submit responses. Please try again.");
    }
  };

  useEffect(() => {
    if (completedSections.External && completedSections.Internal) {
      updateSubTask(taskId);
      onClose();
    }
  }, [completedSections, taskId]);

  useEffect(() => {
    const updateResponsesForCompletedSection = (sectionName, questionsArray) => {
      setResponses((prevResponses) => {
        const existing = prevResponses.filter((r) => r.section !== sectionName);
        const newEntries = questionsArray.map((q) => ({
          section: sectionName,
          question: q,
          condition: "Yes",
          comments: "",
          date: selectedDate,
        }));
        return [...existing, ...newEntries];
      });
    };

    if (completedSections.External) {
      updateResponsesForCompletedSection("External", externalQuestions);
    }

    if (completedSections.Internal) {
      updateResponsesForCompletedSection("Internal", internalQuestions);
    }
  }, [completedSections.External, completedSections.Internal]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Make the API request
        const response = await axios.get("/api/auth/users");
        // Filter users with roleid === 4 and update state
        const filteredUsers = response.data.filter((usera) => usera.id !== user?.id);
        setUsers(filteredUsers);
      } catch (error) {
        console.error("Error fetching tasks:", error);
      }
    };

    fetchUsers();
  }, [user?.id]);

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{ fontSize: "14px", backgroundColor: "#F2F6FF", color: "#1751D0" }}
        label='Weekly Tasks'
      />

      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 1 }}>
        Weekly Building Audit
      </Typography>

      <Typography variant='subtitle2' sx={{ color: "#757575", mb: 1 }}>
        Date: {currentDate} &nbsp; • &nbsp; <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>

      <ToggleButtonGroup
        value={section}
        exclusive
        onChange={(event, newSection) => {
          setSection(newSection);
          setCurrentQuestionIndex(0);
          setCondition(null);
          setComments("");
        }}
        sx={{ mb: 2 }}
      >
        <ToggleButton value='External' sx={{ textTransform: "none" }} color='primary'>
          External
        </ToggleButton>
        <ToggleButton value='Internal' sx={{ textTransform: "none" }} color='primary'>
          Internal
        </ToggleButton>
      </ToggleButtonGroup>

      {completedSections[section] ? (
        <Typography variant='subtitle2' sx={{ color: "#1751D0", fontSize: "14px", mb: 2 }}>
          {section} Questions {questions.length} of {questions.length} Complete!
        </Typography>
      ) : (
        <Typography variant='subtitle2' sx={{ color: "#757575", fontSize: "14px", mb: 2 }}>
          <Grid container>
            <Grid item xs={7} sx={{ color: "#1751D0" }}>
              {section} Questions {responses.filter((r) => r.section === section).length} of {questions.length}{" "}
              Complete!
            </Grid>
          </Grid>
        </Typography>
      )}

      {currentQuestionIndex === questions.length ? (
        <>
          <Grid container>
            <Grid item xs={10}>
              <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>Has maintenance been informed?</Typography>
            </Grid>
            <Grid item xs={2}>
              <ToggleButtonGroup
                value={maintenanceInformed}
                exclusive
                onChange={(event, value) => setMaintenanceInformed(value)}
              >
                <ToggleButton value='Yes' sx={{ textTransform: "none" }} color='primary'>
                  Yes
                </ToggleButton>
                <ToggleButton value='No' sx={{ textTransform: "none" }} color='error'>
                  No
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
          </Grid>

          <Typography sx={{ fontSize: "14px", color: "black" }}>Date maintenance informed :</Typography>
          <TextField
            type='date'
            variant='outlined'
            size='small'
            value={dateInformed}
            onChange={(e) => setDateInformed(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Typography sx={{ fontSize: "14px", mb: 2, color: "black" }}>
            Name of the person who informed the maintenance :
          </Typography>
          <Autocomplete
            id='name'
            options={users}
            getOptionLabel={(option) => option?.username || ""} // ✅ Display username
            value={users.find((u) => u.username === informerName) || null} // ✅ Maintain object for proper matching
            onChange={(event, newValue) => {
              setInformerName(newValue?.username || ""); // ✅ Store just username
            }}
            renderInput={(params) => <TextField {...params} placeholder='Select a user' />}
            fullWidth
          />
        </>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={6}>
            {questions.map((question, index) => {
              const response = responses.find(
                (response) => response.question === question && response.section === section
              );

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <Typography sx={{ flex: 1, color: "black", fontSize: "14px" }}>{question}</Typography>
                  <IconButton
                    color='primary'
                    onClick={() => {
                      if (response) {
                        // Remove the response for this question
                        setResponses((prev) => prev.filter((r) => !(r.question === question && r.section === section)));
                      } else {
                        // Add the response for this question
                        setResponses((prev) => [...prev, { section, question, condition: "Yes" }]);
                      }
                    }}
                    sx={{ color: response ? "#1751D0" : "#E0E0E0" }}
                  >
                    <CheckCircleIcon />
                  </IconButton>
                </Box>
              );
            })}
          </Grid>

          <Grid item xs={6} md={6}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={10}>
                    <Typography sx={{ fontSize: "14px", color: "black" }}>{questions[currentQuestionIndex]}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <ToggleButtonGroup value={condition} exclusive onChange={handleConditionChange}>
                      <ToggleButton value='Yes' sx={{ textTransform: "none" }} color='primary'>
                        Yes
                      </ToggleButton>
                      <ToggleButton value='No' sx={{ textTransform: "none" }} color='error'>
                        No
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <TextField
              fullWidth
              select
              label='Select Action From Here'
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              sx={{ mt: 2 }}
              disabled={condition === "Yes"}
            >
              <MenuItem value='Inform to the manager'>Inform to the manager</MenuItem>
              <MenuItem value='Refill'>Refill</MenuItem>
            </TextField>

            <Typography variant='h6' sx={{ mt: 3, fontWeight: "bold" }}>
              Building Audit Report Damage
            </Typography>
            <Box sx={{ mt: 2, mb: 2 }}>
              {/* Table Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  backgroundColor: "#E3F2FD",
                  padding: "8px 16px",
                  borderRadius: "8px",
                }}
              >
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={2}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>Time</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>Date</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>Damage Category</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>Status</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography sx={{ fontSize: "14px", color: "blue" }}>Attachment</Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Table Content */}
              {condition === "No" &&
                records.map((record, index) => (
                  <Card key={index} sx={{ mt: 1, boxShadow: 1 }}>
                    <CardContent>
                      <Grid container alignItems='center' spacing={2}>
                        <Grid item xs={2}>
                          <Typography sx={{ fontSize: "14px", color: "black" }}>{record.time}</Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography sx={{ fontSize: "14px", color: "black" }}>{record.date}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography sx={{ fontSize: "14px", color: "black" }}>{record.damageType}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "black",
                              wordBreak: "break-word",
                              whiteSpace: "normal",
                              overflowWrap: "break-word",
                            }}
                          >
                            {record.comments}
                          </Typography>
                        </Grid>
                        <Grid item xs={2} textAlign='center'>
                          <IconButton color='primary'>
                            <CameraAltIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ))}
            </Box>

            <Button color='primary' disabled={condition !== "No"} onClick={() => setDialogOpen(true)}>
              <AddIcon /> Add another record
            </Button>
          </Grid>
          <ReusableDialog
            isOpen={dialogOpen}
            onClose={() => setDialogOpen(false)}
            title='Building Audit Report Damage'
            onSubmit={handleSubmitDamage}
            submitText='Submit'
            cancelText='Cancel'
          >
            <Box sx={{ mb: 2 }}>
              <ToggleButtonGroup value={damageType} exclusive onChange={handleDamageTypeChange}>
                <ToggleButton value='external' sx={{ textTransform: "none" }} color='primary'>
                  External Damage
                </ToggleButton>
                <ToggleButton value='interior' sx={{ textTransform: "none" }} color='primary'>
                  Interior Damage
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Typography sx={{ fontSize: "14px", mb: 1 }}>Comment:</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              variant='outlined'
              sx={{ mb: 3 }}
            />

            <Typography sx={{ fontSize: "14px", mb: 1 }}>If any Attachment:</Typography>
            <Box
              sx={{
                border: "2px dashed #ccc",
                padding: 3,
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {/* Hidden File Input */}
              <input
                type='file'
                accept='image/*'
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />

              {/* Icons for Upload and Camera */}
              <IconButton onClick={() => fileInputRef.current.click()}>
                <ImageIcon sx={{ fontSize: 40, color: "#757575" }} />
              </IconButton>

              <IconButton onClick={() => setIsCameraOpen(true)}>
                <PhotoCameraIcon sx={{ fontSize: 40, color: "#757575" }} />
              </IconButton>

              {/* Camera Capture */}
              {isCameraOpen && (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <Webcam
                    ref={webcamRef}
                    screenshotFormat='image/png'
                    videoConstraints={{
                      width: 300, // Adjust width
                      height: 200, // Adjust height
                      facingMode: "user", // Front camera
                    }}
                    style={{
                      width: 300, // Adjust width
                      height: 200, // Adjust height
                      borderRadius: "8px", // Optional: rounded corners
                      border: "2px solid #ccc", // Optional: border
                    }}
                  />
                  <IconButton onClick={capture}>
                    <PhotoCameraIcon sx={{ fontSize: 30, color: "#ff5722", mt: 1 }} />
                  </IconButton>
                </Box>
              )}

              {/* Display the Uploaded or Captured Image */}
              {imageSrc ? (
                <>
                  <br />
                  <img
                    src={imageSrc}
                    alt='Selected or Captured'
                    style={{
                      width: 300, // Adjust width
                      height: 200, // Adjust height
                      borderRadius: "8px", // Optional: rounded corners
                      border: "2px solid #ccc", // Optional: border
                    }}
                  />
                </>
              ) : (
                <Typography sx={{ color: "#757575", marginTop: 1 }}>Select an image or capture a photo</Typography>
              )}
            </Box>
          </ReusableDialog>
        </Grid>
      )}

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          position: "sticky",
          bottom: 0,
          padding: 2,
          zIndex: 10,
        }}
      >
        {/* Cancel Button */}
        {currentQuestionIndex === 0 && (
          <Button variant='outlined' color='primary' onClick={() => onClose()}>
            Cancel
          </Button>
        )}
        {/* Back Button */}
        {currentQuestionIndex > 0 && (
          <Button variant='outlined' color='primary' onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}>
            Previous
          </Button>
        )}
        {/* Next/Submit Button */}
        {currentQuestionIndex === questions.length ? (
          <Button
            variant='contained'
            color='primary'
            onClick={handleSubmit}
            disabled={!maintenanceInformed || !dateInformed || !informerName}
          >
            Submit
          </Button>
        ) : (
          <Button variant='contained' color='primary' onClick={handleNext} disabled={!condition}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default WeeklyBuildingAudit;
